import { Injectable, NgZone } from '@angular/core';
import AgoraRTC, { IAgoraRTCClient, ICameraVideoTrack, IMicrophoneAudioTrack, IAgoraRTCRemoteUser } from 'agora-rtc-sdk-ng';
import { Notify } from 'notiflix';
import { environment } from 'src/environments/environment';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root'
})
export class AgoraService {
  client: IAgoraRTCClient | null = null;
  localAudioTrack: IMicrophoneAudioTrack | null = null;
  localVideoTrack: ICameraVideoTrack | null = null;
  remoteUsers: IAgoraRTCRemoteUser[] = [];

  token: string | null = null;
  channel: string | null = null;
  uid: any = null;
  caller: any = null;
  public showStopButton: boolean = false;
  private isJoining: boolean = false;
  private pttClient: IAgoraRTCClient | null = null;
  private pttAudioTrack: IMicrophoneAudioTrack | null = null;
  private isPTTActive = false;

  constructor(private _http: HttpService, private ngZone: NgZone) {
    this.configureServeArea();
    AgoraRTC.setLogLevel(1);
  }

  public initAndStartCall(
    isVideoEnabled: boolean,
    caller: any,
    channelId?: string,
    socketToken?: { rtcToken?: string; rtcUid?: any }
  ) {
    if (this.showStopButton || this.isJoining) {
      console.warn('Agora: Call in progress or joining. Ignoring init.');
      return;
    }

    this.isJoining = true;

    // Log exactly what we received so token/UID issues are immediately visible
    console.log('Agora: initAndStartCall called with:', {
      channelId,
      rtcToken: socketToken?.rtcToken ? socketToken.rtcToken.slice(0, 12) + '...' : 'MISSING',
      rtcUid: socketToken?.rtcUid ?? 'MISSING'
    });

    if (socketToken?.rtcToken && socketToken?.rtcUid) {
      this.startCall(
        isVideoEnabled,
        caller,
        channelId || null,
        this.sanitizeParam(socketToken.rtcToken),
        this.sanitizeParam(socketToken.rtcUid)
      );
    } else {
      console.warn('Agora: socketToken incomplete — falling back to API token fetch.', socketToken);
      const payload = channelId ? { channelId } : {};
      this._http.rpost('agora/getAuthToken', payload).subscribe(
        (res: any) => {
          const d = res?.data;
          this.startCall(
            isVideoEnabled,
            caller,
            d?.channelId || channelId,
            this.sanitizeParam(d?.rtcToken),
            this.sanitizeParam(d?.rtcUid)
          );
        },
        () => {
          this.isJoining = false;
          Notify.failure('Failed to get call token');
        }
      );
    }
  }

  public async startCall(
    isVideoEnabled: boolean,
    caller: any,
    channel: string | null,
    rtcToken: string | null,
    rtcUid: any
  ) {

    this.caller = caller;
    this.channel = channel ? channel.trim() : null;
    this.token = rtcToken ? rtcToken.trim() : null;

    const appId = environment.agoraAppId ? environment.agoraAppId.trim() : null;

    // ── Hard validation before touching the SDK ────────────────────────────
    if (!appId || appId === 'undefined') {
      Notify.failure('Agora App ID not found');
      this.isJoining = false;
      return;
    }

    if (!this.channel) {
      Notify.failure('Call Error: Channel name is missing');
      this.isJoining = false;
      return;
    }

    if (!this.token) {
      Notify.failure('Call Error: RTC Token is missing or invalid');
      this.isJoining = false;
      return;
    }

    if (!this.isValidToken(this.token)) {
      Notify.failure('Call Error: RTC Token format is invalid');
      console.error('Agora: Invalid token format:', this.token?.slice(0, 20));
      this.isJoining = false;
      return;
    }

    if (!rtcUid) {
      Notify.failure('Call Error: RTC UID is missing');
      console.error('Agora: rtcUid is null/undefined — token was likely generated for a different UID');
      this.isJoining = false;
      return;
    }

    // ── UID: convert to number and mask to 31-bit safe range ──────────────
    // IMPORTANT: The masked UID MUST match what the backend used when generating the token.
    // If your backend does NOT mask the UID, remove the masking here and just use rawUid.
    const rawUid = Number(rtcUid);
    if (isNaN(rawUid) || rawUid <= 0) {
      Notify.failure('Call Error: RTC UID is not a valid number');
      console.error('Agora: Invalid rtcUid value:', rtcUid);
      this.isJoining = false;
      return;
    }
    // Use the UID exactly as the backend sent it — do NOT mask unless your backend also masks
    const finalUid: number = rawUid;

    // ── Diagnostics ────────────────────────────────────────────────────────
    console.log('========== AGORA JOIN DIAGNOSTICS ==========');
    console.log('AppId          :', appId.substring(0, 8) + '...');
    console.log('Channel        :', this.channel);
    console.log('Token Prefix   :', this.token.slice(0, 3));
    console.log('Token Length   :', this.token.length);
    console.log('Token Valid    :', this.isValidToken(this.token) ? '✅' : '❌');
    console.log('Raw UID        :', rawUid);
    console.log('Final UID      :', finalUid, '(after 31-bit mask)');
    console.log('Token (first 12):', this.token.slice(0, 12));
    console.log('Protocol       :', window.location.protocol);
    console.log('Secure Context :', window.isSecureContext);
    console.log('SDK Version    :', AgoraRTC.VERSION);
    console.log('=============================================');

    const systemOk = AgoraRTC.checkSystemRequirements();
    if (!systemOk) {
      Notify.failure('Your browser does not support Agora RTC');
      this.isJoining = false;
      return;
    }

    try {
      await this.ngZone.runOutsideAngular(async () => {

        // ── Tear down any existing client ──────────────────────────────────
        if (this.client) {
          try {
            if (this.client.connectionState !== 'DISCONNECTED') {
              await this.client.leave();
            }
          } catch (e) {
            console.warn('Agora: Previous client leave error (ignored):', e);
          }
          this.client = null;
        }

        this.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        this.setupClientEvents(this.client);

        // ── Join ───────────────────────────────────────────────────────────
        console.log('Agora: Attempting join — channel:', this.channel, '| UID:', finalUid);
        try {
          await this.client.join(appId, this.channel!, this.token!, finalUid);
          console.log('Agora: ✅ Join SUCCESS — UID:', finalUid, 'Channel:', this.channel);
        } catch (joinErr: any) {
          // Normalize the Agora SDK's internal crash ("rollback undefined" TypeError)
          // This happens when the server rejects the join and the SDK's own error
          // handler crashes trying to read .code off an undefined response object.
          throw this.normalizeJoinError(joinErr);
        }

        // ── Create and publish local tracks ───────────────────────────────
        if (isVideoEnabled) {
          [this.localAudioTrack, this.localVideoTrack] =
            await AgoraRTC.createMicrophoneAndCameraTracks(
              {},
              { encoderConfig: '720p_3' }
            );
        } else {
          this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        }

        const tracks: any[] = [];
        if (this.localAudioTrack) tracks.push(this.localAudioTrack);
        if (this.localVideoTrack) tracks.push(this.localVideoTrack);

        if (tracks.length > 0) {
          await this.client.publish(tracks);
          console.log('Agora: ✅ Tracks published');
        }

        // Subscribe to any users who published before we joined
        for (const remoteUser of this.client.remoteUsers) {
          if (remoteUser.hasAudio) {
            await this.client.subscribe(remoteUser, 'audio');
            remoteUser.audioTrack?.play();
            console.log('Agora: late-subscribe audio from UID:', remoteUser.uid);
          }
          if (remoteUser.hasVideo) {
            await this.client.subscribe(remoteUser, 'video');
            setTimeout(() => remoteUser.videoTrack?.play(`agora_remote${remoteUser.uid}`), 500);
            console.log('Agora: late-subscribe video from UID:', remoteUser.uid);
          }
        }
      });

      this.ngZone.run(() => {
        this.showStopButton = true;
        this.playLocalStream();
      });

    } catch (err: any) {
      console.error('Agora: startCall failed:', err);
      this.handleAgoraError(err);
      await this.safeCleanup();
    } finally {
      this.isJoining = false;
    }
  }

  /**
   * Normalizes the Agora SDK's internal crash into a readable, actionable error.
   *
   * When Agora's signaling server rejects a join (bad token, UID mismatch, etc.),
   * the SDK attempts an internal rollback but the rollback handler is undefined,
   * causing: "TypeError: Cannot read properties of undefined (reading 'code')"
   * This TypeError is what surfaces in your catch block instead of the real reason.
   */
  private normalizeJoinError(joinErr: any): Error {
    console.error('Agora: Raw join error:', joinErr);
    console.error('Agora: Join error type:', typeof joinErr);
    console.error('Agora: Join error message:', joinErr?.message);
    console.error('Agora: Join error code:', joinErr?.code);

    // Case 1: SDK internal crash — "rollback undefined" TypeError
    // Real cause is almost always: token/UID mismatch, expired token, or wrong AppID
    if (
      joinErr instanceof TypeError &&
      typeof joinErr.message === 'string' &&
      joinErr.message.includes('undefined')
    ) {
      const normalized = new Error(
        'Agora channel join was rejected by the server. ' +
        'Most likely cause: token/UID mismatch between caller and callee, or expired token. ' +
        'Verify that the rtcUid passed to client.join() exactly matches the UID used when generating the token on the backend. ' +
        '[SDK internal: ' + joinErr.message + ']'
      );
      (normalized as any).code = 'JOIN_REJECTED_TOKEN_UID_MISMATCH';
      return normalized;
    }

    // Case 2: Standard Agora error with a code
    if (joinErr?.code) {
      const normalized = new Error(
        `Agora join failed with code ${joinErr.code}: ${joinErr.message || 'No message'}`
      );
      (normalized as any).code = joinErr.code;
      return normalized;
    }

    // Case 3: Pass through anything else
    return joinErr instanceof Error ? joinErr : new Error(String(joinErr));
  }

  private handleAgoraError(err: any) {
    let errName    = 'UnknownError';
    let errMessage = 'An unexpected error occurred during the call';
    let errCode    = 'UNKNOWN';
    let stackTrace = 'No stack available';

    try {
      if (!err) {
        errMessage = 'Null or undefined error received from Agora SDK';
      } else if (typeof err === 'string') {
        errMessage = err.length > 0 ? err : 'Empty error string from SDK';
      } else if (err instanceof Error) {
        errName    = String(err.name || 'Error');
        errMessage = String(err.message || 'No message');
        errCode    = String((err as any)?.code || 'ERROR');
        stackTrace = String(err.stack || 'No stack trace');
      } else if (typeof err === 'object') {
        try { errName    = String(err.name ?? err.type ?? 'ObjectError'); }    catch { errName    = 'ObjectError'; }
        try { errMessage = String(err.message ?? err.msg ?? JSON.stringify(err)); } catch { errMessage = 'Unable to stringify error'; }
        try { errCode    = String(err.code ?? err.errorCode ?? err.status ?? 'OBJECT_ERROR'); } catch { errCode = 'OBJECT_ERROR'; }
        try { stackTrace = String(err.stack ?? 'No stack in object'); }        catch { stackTrace = 'Unable to get stack'; }
      } else {
        errMessage = `SDK raised non-standard error of type ${typeof err}: ${String(err)}`;
      }
    } catch (parseErr) {
      console.error('Agora: Error while parsing error object:', parseErr);
      errMessage = 'Critical error while parsing SDK error response';
    }

    if (typeof errMessage === 'string' && errMessage.length > 500) {
      errMessage = errMessage.substring(0, 500);
    }

    console.error('Agora ERROR DETAILS:');
    console.error('  Name      :', errName);
    console.error('  Message   :', errMessage);
    console.error('  Code      :', errCode);
    console.error('  Stack     :', stackTrace);

    // ── Map error codes/messages to user-friendly notifications ───────────
    let userMsg = errMessage;
    const msgLower = typeof errMessage === 'string' ? errMessage.toLowerCase() : '';
    const codeLower = typeof errCode === 'string' ? errCode.toLowerCase() : '';

    if (
      codeLower.includes('token_uid_mismatch') ||
      codeLower.includes('join_rejected') ||
      msgLower.includes('token/uid mismatch') ||
      msgLower.includes('rollback undefined')
    ) {
      userMsg =
        'Call failed: Token/UID mismatch.\n\n' +
        'The token was generated for a different user ID than the one used to join. ' +
        'Please hang up and try again. If the issue persists, contact support.';
    } else if (msgLower.includes('rejected by the server') || msgLower.includes('join was rejected')) {
      userMsg = 'Call failed: Server rejected the join request. Your token may be expired or invalid. Please try again.';
    } else if (codeLower === 'cid_no_authorized' || msgLower.includes('no authorized')) {
      userMsg = 'Call failed: Not authorized to join this channel. Token may be invalid.';
    } else if (codeLower === 'invalid_params' || msgLower.includes('invalid argument')) {
      userMsg = 'Call failed: Invalid call parameters. Please contact support.';
    } else if (msgLower.includes('permission') || msgLower.includes('denied')) {
      userMsg = 'Call failed: Permission denied. Your token may have expired.';
    } else if (msgLower.includes('network') || msgLower.includes('timeout')) {
      userMsg = 'Call failed: Network connection issue. Please check your internet and try again.';
    } else if (msgLower.includes('cannot reach agora')) {
      userMsg = 'Call failed: Cannot reach call servers. Please check your network or firewall.';
    }

    this.ngZone.run(() => {
      try {
        const displayMsg = (typeof userMsg === 'string' && userMsg.length > 0)
          ? userMsg
          : `Call failed (${errCode})`;
        Notify.failure(displayMsg);
      } catch (notifyErr) {
        console.error('Agora: Failed to show notification:', notifyErr);
        try { Notify.failure('Call failed. Please try again.'); } catch { }
      }
      this.showStopButton = false;
    });
  }

  private async safeCleanup() {
    try {
      if (this.localAudioTrack) { this.localAudioTrack.close(); this.localAudioTrack = null; }
      if (this.localVideoTrack) { this.localVideoTrack.close(); this.localVideoTrack = null; }
      this.remoteUsers = [];

      if (this.client) {
        if (this.client.connectionState !== 'DISCONNECTED') {
          console.log('Agora: Safe leave from state:', this.client.connectionState);
          await this.client.leave().catch(e => console.warn('Agora: Leave error (ignored):', e));
        }
        this.client = null;
      }
    } catch (e) {
      console.error('Agora: Cleanup error:', e);
    }
    this.ngZone.run(() => {
      this.showStopButton = false;
      this.isJoining = false;
    });
  }

  public playRemoteStreams() {
    this.remoteUsers.forEach(u => {
      if (u.videoTrack) {
        const el = document.getElementById('agora_remote_main');
        if (el) u.videoTrack.play('agora_remote_main');
      }
    });
  }

  public playLocalStream() {
    if (this.localVideoTrack) this.localVideoTrack.play('agora_local');
  }

  public async enableVideo(): Promise<void> {
    if (!this.client || this.client.connectionState !== 'CONNECTED') {
      console.warn('Agora: enableVideo called but client not connected');
      return;
    }
    try {
      if (!this.localVideoTrack) {
        this.localVideoTrack = await AgoraRTC.createCameraVideoTrack();
        await this.client.publish([this.localVideoTrack]);
      }
      this.localVideoTrack.setMuted(false);
      this.ngZone.run(() => {
        setTimeout(() => this.playLocalStream(), 500);
      });
    } catch (e) {
      console.error('Agora: enableVideo error:', e);
      Notify.failure('Failed to enable camera');
    }
  }

  public async stop() {
    await this.safeCleanup();
  }

  public async startPTT(channelId: string, token: string, uid: number, publishMic: boolean = true): Promise<void> {
    if (!this.pttClient) {
      this.pttClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      this.pttClient.on('user-published', async (user, mediaType) => {
        if (mediaType === 'audio') {
          await this.pttClient!.subscribe(user, 'audio');
          user.audioTrack?.play();
        }
      });
      await this.pttClient.join(environment.agoraAppId, channelId, token, uid);
    }
    if (publishMic) {
      if (!this.pttAudioTrack) {
        this.pttAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      }
      await this.pttClient.publish(this.pttAudioTrack);
    }
  }

  public async stopPTT(): Promise<void> {
    if (this.pttAudioTrack) {
      await this.pttClient?.unpublish(this.pttAudioTrack);
      this.pttAudioTrack.close();
      this.pttAudioTrack = null;
    }
    // Don't leave — stay in channel to listen
  }

  public async leavePTTChannel(): Promise<void> {
    await this.pttClient?.leave();
    this.pttClient = null;
    this.isPTTActive = false;
  }

  public startPTTForContact(myId: string, contactId: string): void {
    if (this.isPTTActive) { console.warn('PTT already active'); return; }
    this.isPTTActive = true;
    this.playPTTBeep();
    const sorted = [myId, contactId].sort().join('');
    let hash = 0;
    for (let i = 0; i < sorted.length; i++) {
      hash = ((hash << 5) - hash) + sorted.charCodeAt(i);
      hash |= 0;
    }
    const channelId = 'ptt' + Math.abs(hash).toString(16).padStart(8, '0');
    console.log('PTT channelId:', channelId, 'myId:', myId, 'contactId:', contactId);

    this._http.rpost('agora/getAuthToken', { channelId, userId: myId }).subscribe((res: any) => {
      const d = res?.data;
      if (!d?.rtcToken || !d?.rtcUid) { Notify.failure('PTT token failed.'); return; }
      this.startPTT(channelId, d.rtcToken, d.rtcUid, true).catch(e => {
        console.error('PTT start error:', e);
        Notify.failure('PTT failed to start.');
      });
    });
  }

  public joinPTTAsReceiver(channelId: string, myId: string): void {
    this._http.rpost('agora/getAuthToken', { channelId, userId: myId }).subscribe((res: any) => {
      const d = res?.data;
      if (!d?.rtcToken || !d?.rtcUid) return;
      this.startPTT(channelId, d.rtcToken, d.rtcUid, false).catch(e => console.warn('PTT receiver join error:', e));
    });
  }

  private playPTTBeep(): void {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      // Classic walkie-talkie chirp: fast rise from 800→1400Hz with quick fade
      osc.type = 'square';
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(1400, ctx.currentTime + 0.08);
      osc.frequency.linearRampToValueAtTime(1100, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.18);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.18);
      osc.onended = () => ctx.close();
    } catch (e) { /* audio not available */ }
  }

  private setupClientEvents(client: IAgoraRTCClient) {
    this.ngZone.runOutsideAngular(() => {
      client.on('user-published', async (user, mediaType) => {
        console.log('Agora: user-published fired — UID:', user.uid, 'mediaType:', mediaType);
        await client.subscribe(user, mediaType);
        console.log('Agora: subscribed to', mediaType, 'from UID:', user.uid);
        if (mediaType === 'video') {
          this.ngZone.run(() => {
            if (!this.remoteUsers.find(u => u.uid === user.uid)) {
              this.remoteUsers.push(user);
            }
          });
          setTimeout(() => user.videoTrack?.play('agora_remote_main'), 500);
        }
        if (mediaType === 'audio') user.audioTrack?.play();
      });

      client.on('user-left', (user) => {
        this.ngZone.run(() => {
          this.remoteUsers = this.remoteUsers.filter(u => u.uid !== user.uid);
        });
      });
    });
  }

  private sanitizeParam(val: any): any {
    return val === 'undefined' || val === 'null' || !val ? null : val;
  }

  private configureServeArea() {
    // Do not set area — let Agora use default global servers
  }

  private isValidToken(token: any): boolean {
    if (!token || typeof token !== 'string') return false;
    if (token.length < 20) return false;
    const validPrefixes = ['007', '006'];
    return validPrefixes.includes(token.slice(0, 3));
  }
}